import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Settings2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { BILLING_LABELS, formatBRL, openPortal, startCheckout, startPix } from "@/lib/billing";
import { PayButtons } from "@/components/app/PayButtons";
import { formatDate } from "@/lib/services";

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between border-b border-slate-800 py-2 text-sm"><span className="text-slate-400">{label}</span><span className="font-medium" data-testid={testid}>{value}</span></div>
);

export const BillingDialog = ({ open, onClose }) => {
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) api.get("/billing").then((r) => setB(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, [open]);

  const pay = async (fn, kind) => {
    setLoading(kind);
    try {
      await fn();
    } catch (err) {
      toast.error(errorMessage(err));
      setLoading(false);
    }
  };

  const pix = b?.method === "pix";
  const canPay = b && !b.exempt && (pix || !b.subscribed || b.expired || ["past_due", "canceled"].includes(b.status));
  const canManage = b && !b.exempt && b.subscribed && b.status !== "canceled";
  const cardRunning = canManage && b.status === "active" && !b.cancel_at_period_end;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-slate-700 bg-slate-900 sm:max-w-md" data-testid="billing-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">Assinatura</DialogTitle>
          <DialogDescription>Mensalidade no cartão (automática) ou por Pix (você paga a cada mês). A empresa é bloqueada no dia seguinte ao vencimento sem pagamento.</DialogDescription>
        </DialogHeader>
        {!b ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /> : (
          <div>
            <Row label="Situação" value={b.exempt ? "Isenta" : BILLING_LABELS[b.status] || b.status} testid="billing-status" />
            {!b.exempt && <Row label="Forma de pagamento" value={pix ? "Pix mensal" : b.subscribed ? "Cartão" : "—"} testid="billing-method" />}
            {!b.exempt && <Row label={b.subscribed && b.status === "active" ? "Próxima cobrança" : pix ? "Pago até" : "Acesso liberado até"} value={formatDate(b.paid_until)} testid="billing-paid-until" />}
            {!b.exempt && <Row label="Valor" value={`${formatBRL(b.monthly_price)}/mês`} testid="billing-price" />}
            {b.cancel_at_period_end && b.status !== "canceled" && (
              <p className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="billing-cancel-scheduled">
                Cancelamento agendado: o acesso continua até {formatDate(b.paid_until)} e depois a empresa é bloqueada. Para desistir do cancelamento, use "Gerenciar assinatura".
              </p>
            )}
            {canManage && (
              <Button variant="outline" onClick={() => pay(openPortal, "portal")} disabled={!!loading} className="mt-6 h-11 w-full rounded-full border-slate-600 bg-transparent font-semibold" data-testid="billing-manage-button">
                {loading === "portal" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Settings2 className="mr-2 h-4 w-4" />} Gerenciar assinatura (cartão ou cancelar)
              </Button>
            )}
            {canPay && !cardRunning && <PayButtons loading={loading} onCard={() => pay(startCheckout, "card")} onPix={() => pay(startPix, "pix")} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
